import React, { useEffect, useRef, useState } from 'react';
import styles from '../styles/dialog.module.css';
import { resolveMessage } from '../i18n/translate';

interface PromptDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (value: string) => void;
  title?: string;
  message: string;
  placeholder?: string;
  defaultValue?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info' | 'success';
}

const PromptDialog: React.FC<PromptDialogProps> = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  message,
  placeholder,
  defaultValue = '',
  confirmText,
  cancelText,
  variant = 'info',
}) => {
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    setValue(defaultValue);
    inputRef.current?.focus();
  }, [isOpen, defaultValue]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`${styles.dialog} ${styles[variant]}`} role="dialog" aria-modal="true">
        {title && (
          <div className={styles.header}>
            <h3 className={styles.title}>{title}</h3>
          </div>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit(value);
          }}
        >
          <div className={styles.content}>
            <label className={styles.message}>
              {message}
              <input
                ref={inputRef}
                className={styles.input}
                value={value}
                placeholder={placeholder}
                onChange={(e) => setValue(e.target.value)}
              />
            </label>
          </div>
          <div className={styles.actions}>
            <button type="button" className={`${styles.button} ${styles.secondary}`} onClick={onClose}>
              {cancelText ?? resolveMessage('dialog.cancel')}
            </button>
            <button
              type="submit"
              className={`${styles.button} ${styles.primary} ${variant === 'danger' ? styles.danger : ''}`}
            >
              {confirmText ?? resolveMessage('dialog.ok')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PromptDialog;
